import { tradingSocketWorkerManifest } from './contract';
import type { TradingSocketWorkerContract, TradingSocketWorkerState } from './contract';
/**
 * TradingSocketConnection — the one worker client this tab owns (ADR 0016 increment ②).
 *
 * A singleton that spawns the trading worker over the composition-bound transport (per-tab
 * dedicated worker on web; `ipcWorkerTransport` when Electron converts) and owns everything
 * per-tab about it: the connection/authentication signals, the order event streams, routing of
 * `authentication-rejected` into `SessionService.refresh()`, and the account-target ledger.
 *
 * Multiple `TradingSocketService` façades (one per consumer, transient) register their account
 * sets here; the connection reconciles the union with the worker — the union moved main-side
 * when the worker stopped being shared, replacing the SharedWorker port registry.
 *
 * Session custody: the worker receives session state via the `session` relay that
 * `connectWorker` resolves from the container — this class never sends tokens.
 */
import { TradingSocketError } from './errors';
import { effect, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import { TypedContainer } from '@thinkalpha/common/ioc/TypedContainer.js';
import { NewOrderBase, NewOrderBatch } from '@thinkalpha/trading-ws-client/contracts/order.js';
import type { Order, OrderUpdate, OrderUpdateReport } from '@thinkalpha/trading-ws-client/contracts/order.js';
import { inject, injectable } from 'inversify';
import { Subject } from 'rxjs';
import type { Observable } from 'rxjs';
import { getDatadogSessionId } from 'src/lib/datadogSession';
import { connectReducerDevtools } from 'src/lib/devtools';
import type { ReducerDevtoolsConnection } from 'src/lib/devtools';
import type { SendOrderData } from 'src/routes/widgets/TradeProWidget/api';
import { connectWorker } from 'src/services/workerHost/client';
import type { WorkerClient, WorkerStateRelayBindings } from 'src/services/workerHost/client';
import { WorkerRequestError } from 'src/services/workerHost/errors';
import type { WorkerStateRelay } from 'src/services/workerHost/stateRegistry';
import { dedicatedWorkerTransport } from 'src/services/workerHost/transports/dedicatedWorkerTransport';
import type { WorkerTransport } from 'src/services/workerHost/transports/types';
import type { ReactBindings } from 'src/types/bindings';
import { v4 } from 'uuid';
import { z } from 'zod';

/** Serializable snapshot of the worker's state for Redux DevTools */
interface TradingSocketDevtoolsState {
    workerState: TradingSocketWorkerState;
    isConnected: boolean;
    isAuthenticated: boolean;
    joinedAccountCount: number;
}

interface TradingSocketDevtoolsAction {
    type: string;
}

/**
 * One consumer's registration in the account-target ledger. Disposing the slot removes its
 * accounts from the union and reconciles.
 */
export interface TradingAccountsSlot extends Disposable {
    set(accountIds: readonly string[]): void;
    clear(): void;
}

export interface TradingSocketConnection {
    readonly workerState: ReadonlySignal<TradingSocketWorkerState>;
    readonly isConnected: ReadonlySignal<boolean>;
    readonly isAuthenticated: ReadonlySignal<boolean>;
    readonly joinedAccounts: ReadonlySignal<Set<string>>;
    /** Observable, deliberately: only the connection may emit into (or complete) the shared streams. */
    readonly orderUpdates$: Observable<OrderUpdateReport & OrderUpdate>;
    readonly orderSnapshots$: Observable<Order>;
    createAccountsSlot(): TradingAccountsSlot;
    createOrder(data: SendOrderData): Promise<Order>;
    createBatchOrders(orders: readonly SendOrderData[]): Promise<Order[]>;
}

/** The production transport: a per-tab dedicated worker. Bound as `TradingSocketWorkerTransport`. */
export const createTradingSocketWorkerTransport = (): WorkerTransport =>
    dedicatedWorkerTransport(
        () => new Worker(new URL('./worker.ts', import.meta.url), { type: 'module', name: 'trading-socket-worker' }),
    );

@injectable()
export class TradingSocketConnectionImpl implements TradingSocketConnection, Disposable {
    readonly #stack = new DisposableStack();
    readonly #log: ReactBindings['Logger'];
    readonly #sessionService: ReactBindings['SessionService'];
    readonly #client: WorkerClient<TradingSocketWorkerContract>;

    readonly #workerState = signal<TradingSocketWorkerState>('initial');
    readonly #isConnected = signal(false);
    readonly #isAuthenticated = signal(false);
    readonly #joinedAccounts = signal<Set<string>>(new Set());

    readonly #orderUpdates$ = new Subject<OrderUpdateReport & OrderUpdate>();
    readonly #orderSnapshots$ = new Subject<Order>();

    /** Account targets per live slot; the worker receives the union. */
    readonly #accountTargets = new Map<symbol, ReadonlySet<string>>();
    #reconcilingAccounts = false;
    #reconcileQueued = false;

    /** Dedupe rejection reports: one refresh per distinct rejected token. */
    #lastRejectedToken: string | null = null;

    #devtools: ReducerDevtoolsConnection<TradingSocketDevtoolsState, TradingSocketDevtoolsAction> | undefined;

    constructor(
        @inject('Logger') logger: ReactBindings['Logger'],
        @inject('SessionService') sessionService: ReactBindings['SessionService'],
        @inject('CurrentUserModel') currentUserModel: ReactBindings['CurrentUserModel'],
        @inject('WorkerStateRelay:session') sessionRelay: WorkerStateRelay<'session'>,
        @inject('TradingSocketWorkerTransport') transport: WorkerTransport,
    ) {
        this.#log = logger.getSubLogger({ name: 'TradingSocketConnection' }, { component: 'trading' });
        this.#sessionService = sessionService;

        this.#devtools = connectReducerDevtools({ name: 'TradingSocketWorker' });
        if (this.#devtools) {
            this.#stack.use(this.#devtools);
            this.#devtools.init(this.#getDevtoolsState());
        }

        // connectWorker resolves relays from a container; hand it exactly the one this worker
        // composes. Everything from here accumulates on the instance stack — construction is
        // synchronous past this point, so nothing can leak between connect and move-in.
        const relayContainer = new TypedContainer<WorkerStateRelayBindings>();
        relayContainer.bind('WorkerStateRelay:session').toConstantValue(sessionRelay);
        this.#client = this.#stack.use(
            connectWorker(relayContainer, tradingSocketWorkerManifest, transport, {
                metadata: { sessionId: getDatadogSessionId(), userId: currentUserModel.userId.value },
            }),
        );
        this.#client.ready.catch((error: unknown) => {
            this.#log.error('Trading socket worker failed to become ready', {
                error: error instanceof Error ? error : new Error(String(error)),
            });
        });
        this.#stack.defer(
            effect(() => {
                if (this.#client.state.value === 'errored') {
                    // Terminal today (auto-respawn is deliberately not host policy); flags are
                    // cleared so consumers read "disconnected" rather than a stale healthy state.
                    this.#log.error('Trading socket worker died; trading is unavailable until reload', {
                        error: new Error('Trading socket worker transitioned to errored'),
                    });
                    this.#applyWorkerState('initial');
                }
            }),
        );

        const events = this.#client.events;
        this.#stack.use(
            events.on('state-changed', ({ state }) => {
                if (TRACE) {
                    this.#log.trace('Worker state changed', { state });
                }
                this.#applyWorkerState(state);
                this.#devtools?.send({ type: `state-changed:${state}` }, this.#getDevtoolsState());
            }),
        );
        this.#stack.use(
            events.on('authentication-rejected', ({ rejectedToken }) => {
                this.#reportAuthenticationFailure(rejectedToken);
                this.#devtools?.send({ type: 'authentication-rejected' }, this.#getDevtoolsState());
            }),
        );
        this.#stack.use(
            events.on('account.joined', ({ accounts }) => {
                if (TRACE) {
                    this.#log.trace('Accounts joined', { accounts });
                }
                const joined = new Set(this.#joinedAccounts.value);
                for (const accountId of accounts) {
                    joined.add(accountId);
                }
                this.#joinedAccounts.value = joined;
                this.#devtools?.send({ type: 'account.joined' }, this.#getDevtoolsState());
            }),
        );
        this.#stack.use(
            events.on('account.left', ({ accounts }) => {
                if (TRACE) {
                    this.#log.trace('Accounts left', { accounts });
                }
                const joined = new Set(this.#joinedAccounts.value);
                for (const accountId of accounts) {
                    joined.delete(accountId);
                }
                this.#joinedAccounts.value = joined;
                this.#devtools?.send({ type: 'account.left' }, this.#getDevtoolsState());
            }),
        );
        this.#stack.use(
            events.on('order.updated', (orderUpdate) => {
                if (TRACE) {
                    this.#log.trace('Received order update', { orderId: orderUpdate.orderId });
                }
                this.#orderUpdates$.next(orderUpdate);
            }),
        );
        this.#stack.use(
            events.on('order.snapshot', (order) => {
                if (TRACE) {
                    this.#log.trace('Received order snapshot', { orderId: order.id });
                }
                this.#orderSnapshots$.next(order);
            }),
        );

        this.#stack.defer(() => {
            this.#orderUpdates$.complete();
            this.#orderSnapshots$.complete();
        });
    }

    get workerState(): ReadonlySignal<TradingSocketWorkerState> {
        return this.#workerState;
    }

    get isConnected(): ReadonlySignal<boolean> {
        return this.#isConnected;
    }

    get isAuthenticated(): ReadonlySignal<boolean> {
        return this.#isAuthenticated;
    }

    get joinedAccounts(): ReadonlySignal<Set<string>> {
        return this.#joinedAccounts;
    }

    get orderUpdates$(): Observable<OrderUpdateReport & OrderUpdate> {
        return this.#orderUpdates$;
    }

    get orderSnapshots$(): Observable<Order> {
        return this.#orderSnapshots$;
    }

    #getDevtoolsState(): TradingSocketDevtoolsState {
        return {
            workerState: this.#workerState.value,
            isConnected: this.#isConnected.value,
            isAuthenticated: this.#isAuthenticated.value,
            joinedAccountCount: this.#joinedAccounts.value.size,
        };
    }

    #applyWorkerState(state: TradingSocketWorkerState): void {
        this.#workerState.value = state;
        switch (state) {
            case 'initial':
            case 'connecting':
                this.#isConnected.value = false;
                this.#isAuthenticated.value = false;
                break;
            case 'connected':
            case 'authenticating':
                this.#isConnected.value = true;
                this.#isAuthenticated.value = false;
                break;
            case 'authenticated':
            case 'reconciling':
                this.#isConnected.value = true;
                this.#isAuthenticated.value = true;
                break;
        }
        if (!this.#isAuthenticated.value && this.#joinedAccounts.value.size > 0) {
            // Joins are authentication-scoped server-side; a disconnect/clear/rejection
            // invalidates them all, and the next reconcile re-joins.
            this.#joinedAccounts.value = new Set();
        }
    }

    #reportAuthenticationFailure(rejectedToken: string): void {
        if (rejectedToken === this.#lastRejectedToken) {
            return;
        }
        this.#lastRejectedToken = rejectedToken;
        this.#log.warn('Trading server rejected the session token; requesting refresh', {});
        void this.#sessionService.refresh(rejectedToken).catch((error: unknown) => {
            this.#log.error('Failed to report trading socket token rejection', {
                error: error instanceof Error ? error : new Error(String(error)),
            });
        });
    }

    createAccountsSlot(): TradingAccountsSlot {
        const handle = Symbol('trading-accounts-slot');
        const apply = (accountIds: readonly string[]): void => {
            if (this.#stack.disposed) {
                return;
            }
            this.#accountTargets.set(handle, new Set(accountIds));
            this.#reconcileAccounts();
        };
        return {
            set: apply,
            clear: () => apply([]),
            [Symbol.dispose]: () => {
                if (this.#accountTargets.delete(handle)) {
                    this.#reconcileAccounts();
                }
            },
        };
    }

    /** The union across every live slot — what the worker should be joined to. */
    #unionTargets(): string[] {
        return this.#accountTargets
            .values()
            .flatMap((accounts) => accounts.values())
            .unique()
            .toArray();
    }

    #reconcileAccounts(): void {
        void this.#runAccountReconciliation();
    }

    async #runAccountReconciliation(): Promise<void> {
        // If already reconciling, queue another run — the loop below re-reads the union, so a
        // queued run always reconciles the latest targets.
        if (this.#reconcilingAccounts) {
            this.#reconcileQueued = true;
            return;
        }
        this.#reconcilingAccounts = true;
        let retryQueued = false;
        try {
            do {
                this.#reconcileQueued = false;
                const accounts = this.#unionTargets();
                if (TRACE) {
                    this.#log.trace('Reconciling accounts', { accounts });
                }
                await this.#client.request('account.reconcile', { accounts });
            } while (this.#reconcileQueued);
        } catch (error) {
            if (this.#stack.disposed) {
                if (TRACE) {
                    this.#log.trace('Ignoring reconciliation failure after dispose', { error });
                }
                return;
            }
            // A union that arrived behind the failed request still needs the wire; without the
            // retry it would stay dropped until some unrelated later ledger change.
            retryQueued = this.#reconcileQueued;
            this.#log.error('Error reconciling accounts', {
                error: error instanceof Error ? error : new Error(String(error)),
            });
        } finally {
            // Always consumed here: leaving the flag set would cost the next successful run one
            // redundant reconcile.
            this.#reconcileQueued = false;
            this.#reconcilingAccounts = false;
        }
        if (retryQueued) {
            this.#reconcileAccounts();
        }
    }

    async createOrder(data: SendOrderData): Promise<Order> {
        const requestId = v4();

        this.#log.info('Sending order create request to trading socket', {
            requestId,
            account: data.account,
            instrument: data.instrument,
            side: data.side,
            quantity: data.quantity,
        });
        if (TRACE) {
            this.#log.trace('Order create request details', { requestId, data });
        }

        const order = await this.#requestOrder(requestId, () =>
            this.#client.request('order.create', z.decode(NewOrderBase, data)),
        );

        this.#log.info('Order created successfully', { requestId, orderId: order.id });
        return order;
    }

    async createBatchOrders(orders: readonly SendOrderData[]): Promise<Order[]> {
        const requestId = v4();

        this.#log.info('Sending batch order request to trading socket', {
            requestId,
            orderCount: orders.length,
            accounts: orders
                .values()
                .map((order) => order.account)
                .unique()
                .toArray(),
        });
        if (TRACE) {
            this.#log.trace('Batch order request details', { requestId, orders });
        }

        const response = await this.#requestOrder(requestId, () =>
            this.#client.request('order.batch-create', z.decode(NewOrderBatch, { orders: [...orders] })),
        );

        this.#log.info('Batch order creation successful', {
            requestId,
            submittedCount: orders.length,
            createdCount: response.orders.length,
            orderIds: response.orders.map((order) => order.id),
        });
        return response.orders;
    }

    /** Order failures surface as `TradingSocketError` with the worker-side message, as they always have. */
    async #requestOrder<T>(requestId: string, send: () => Promise<T>): Promise<T> {
        try {
            return await send();
        } catch (error) {
            const message =
                error instanceof WorkerRequestError
                    ? error.remote.message
                    : error instanceof Error
                      ? error.message
                      : 'Order request failed';
            this.#log.error('Trading socket order request failed', {
                requestId,
                error: error instanceof Error ? error : new Error(String(error)),
            });
            throw new TradingSocketError(message, requestId);
        }
    }

    [Symbol.dispose](): void {
        if (this.#stack.disposed) {
            return;
        }
        this.#stack.dispose();
    }
}
